/*  MUI  */
import { AutoFixNormal, Check, Refresh } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Grid,
    Paper,
    Skeleton,
    Typography
} from '@mui/material';
import { withTheme } from '@mui/styles';

/*  Shared  */
import { PartnerBackButton } from 'application/Shared/Components/Buttons/BackButton';
import Token from 'application/Shared/Components/CodeBlock/Token';
import { Constants, Sizes } from 'application/Shared/Constants';
import { Notify, Response } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Hooks */
import { getUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

/*  Components  */
import Steps from './Components/Steps';

/*  Styles  */
const sxs = {
    button: {
        generate: { marginBottom: '-20px' }
    },
    grid: {
        apiKeyStep: {
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingTop: '20px'
        }
    },
    icon: {
        check: {
            height: Sizes[3],
            marginLeft: '2px',
            marginTop: '-5px',
            width: Sizes[3]
        },
        generate: {
            marginLeft: '-7px',
            marginRight: '5px',
            marginTop: '-3px'
        },
        regenerate: {
            marginLeft: '-7px',
            marginRight: '5px'
        }
    },
    text: {
        step: { marginLeft: '2px' }
    }
};

/*  State  */
const state = () => ({
    key: '',
    loaded: false,
    secret: '',
    steps: []
});

class API extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        await this.loadAPIKey();
        this.loadSteps();
    };

    unload = () => {
        this.unmount = true;
        this.requests.cancel();
    };

    loadAPIKey = async () => {
        try {
            const response = await axios.get('/apiKey', {
                cancelToken: this.requests.token(),
                params: { scope: 'external_api_default' }
            });
            if (!Response.hasData(response)) {
                return;
            }

            const key = Response.getProperty(response, 'key');
            await new Promise((resolve) => {
                this.setState({ key }, resolve);
            });
        } catch (e) {
            debug.e('Error w/ loadConfig', e.toString());
        }
    };

    loadSteps = (params = {}) => {
        if (this.unmount) {
            return;
        }

        const { t, theme } = this.props;
        const { key } = this.state;
        const { saving } = params;

        const email = Constants.supportEmail;

        const steps = [
            {
                component: (
                    <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                            __html: t('Integrations.API.notice', {
                                colorEmail: theme.palette.text.emailAddress,
                                email
                            })
                        }}
                    />
                ),
                step: false
            },
            {
                component: (
                    <>
                        <span style={sxs.text.step}>
                            {t('Integrations.API.generate_key')}
                            {key && (
                                <Check
                                    style={sxs.icon.check}
                                    color="secondary"
                                />
                            )}
                        </span>
                        {this.renderAPIKeyStep({ saving })}
                    </>
                ),
                step: true
            }
        ];

        this.setState({ loaded: true, steps });
    };

    generateAPIKey = async () => {
        this.loadSteps({ saving: true });

        try {
            const response = await axios.post('/apiKey', {
                overwrite: true,
                scope: 'external_api_default'
            });

            if (!Response.hasData(response)) {
                const err = 'Unable to Generate API Key';
                debug.e(err, response);
                Notify.e(err);
                this.loadSteps();
                return;
            }

            const data = Response.getData(response);
            const { key, secret } = data;

            Notify.s('API key generated.');
            this.setState({ key, secret }, () => {
                this.loadSteps();
            });
        } catch (e) {
            debug.e('Error w/ generateAPIKey', e.toString());
            Notify.e('Unable to generate API key');
            this.loadSteps();
        }
    };

    renderKeyAndSecret = () => {
        const { t } = this.props;
        const { key, secret } = this.state;

        return (
            <Grid container spacing={3} style={sxs.grid.apiKeyStep}>
                <Grid item xs={12}>
                    <Alert severity="success" icon={false}>
                        {t('Integrations.api_key_created')}
                    </Alert>
                </Grid>
                <Grid item xs={12}>
                    <Token label={t('Common.api_key')} token={key} />
                </Grid>
                <Grid item xs={12}>
                    <Token label={t('Common.api_secret')} token={secret} />
                </Grid>
            </Grid>
        );
    };

    renderRegenerateKey = () => {
        const { t } = this.props;
        const { key } = this.state;

        return (
            <Grid container spacing={3} style={sxs.grid.apiKeyStep}>
                <Grid item xs={12}>
                    <Button
                        data-trackid="partner_integrations_api_regenerateKey"
                        variant="contained"
                        color="primary"
                        onClick={() => this.generateAPIKey()}
                    >
                        <Refresh style={sxs.icon.regenerate} fontSize="small" />
                        {t('APIKey.regenerate')}
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <Token label={t('Common.api_key')} token={key} />
                </Grid>
            </Grid>
        );
    };

    renderGenerate = () => {
        const { t } = this.props;

        return (
            <Grid container spacing={3} style={sxs.grid.apiKeyStep}>
                <Grid item xs={12}>
                    <Button
                        data-trackid="partner_integrations_api_generateKey"
                        variant="contained"
                        color="primary"
                        size="large"
                        style={sxs.button.generate}
                        onClick={() => this.generateAPIKey()}
                    >
                        <AutoFixNormal style={sxs.icon.generate} />
                        {t('APIKey.generate')}
                    </Button>
                </Grid>
            </Grid>
        );
    };

    renderAPIKeyStep = (params) => {
        const { key, secret } = this.state;
        const { saving } = params;

        if (saving) {
            return (
                <Grid container spacing={3} style={sxs.grid.apiKeyStep}>
                    <Grid item xs={12}>
                        <Skeleton />
                        <Skeleton animation="wave" />
                        <Skeleton animation={false} />
                    </Grid>
                </Grid>
            );
        }

        if (secret && key) {
            return this.renderKeyAndSecret();
        }

        if (key) {
            return this.renderRegenerateKey();
        }

        return this.renderGenerate();
    };

    render() {
        const { session, t } = this.props;
        const { loaded, steps } = this.state;

        if (!loaded) {
            return null;
        }

        const userTheme = getUserTheme(session?.user?.theme);

        return (
            <>
                <Box width="100%">
                    <Typography variant="h3">
                        {t('Integrations.api')}
                    </Typography>
                    <hr />
                </Box>
                <PartnerBackButton
                    linkTo="/integrations/partnerLevel"
                    textOverride="Integrations.Common.back"
                />
                <Paper
                    elevation={0}
                    variant="outlined"
                    style={{ padding: '20px' }}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <img
                                src={`/images/${userTheme}/integrations/api.png`}
                                alt={t('Integrations.api')}
                                style={{ width: '200px' }}
                            />
                            <hr />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h4">
                                {t('Integrations.instructions')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Steps steps={steps} />
                        </Grid>
                    </Grid>
                </Paper>
            </>
        );
    }
}
export default withTranslation()(withTheme(API));
